import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ParametrosService } from 'app/services/parametros.service';
import { VoucherTypes } from 'app/models/vouchertype.models';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-create-voucher-type',
  templateUrl: './create-voucher-type.component.html',
  styleUrls: ['./create-voucher-type.component.css'],
})
export class CreateVoucherTypeComponent implements OnInit {
  form: UntypedFormGroup;
  type = 'Nuevo';
  isEditMode = false;
  voucherTypeId: number | null = null;
  receiptBookTypes: any[] = [];
  voucherTypesCodeAfip: any[] = [];
  originalDescription = ''; // Para rastrear la descripción original
  userModifiedDescription = false; // Para saber si el usuario modificó manualmente la descripción

  // Opciones para el tipo de operación (cargadas desde API)
  operationTypes: any[] = [];

  // Opciones para el régimen
  regimeOptions = [
    { value: 'general', label: 'Régimen General' },
    { value: 'turismo_rg_3971', label: 'Turismo (RG 3971)' },
    { value: 'alquileres_rg_4004_e', label: 'Alquileres (RG 4004-E)' },
    { value: 'colegios_rg_3950', label: 'Colegios (RG 3950)' },
    { value: 'operaciones_sin_credito_fiscal_rg_4520', label: 'Operaciones que no dan lugar al cómputo del crédito fiscal (RG 4520)' },
    { value: 'factura_credito_electronica_ley_27440', label: 'Factura de crédito electrónica (Ley 27440)' },
    { value: 'factura_electronica_exportacion', label: 'Factura electrónica de exportación' }
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private parametrosService: ParametrosService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(45)]],
      voucherTypesCodeId: [{value: '', disabled: true}, [Validators.required]],
      operationType: ['', [Validators.maxLength(45)]],
      description: ['', [Validators.maxLength(255)]],
      regime: ['general', [Validators.maxLength(250)]],
      affectsStock: [false],
      affectsTreasury: [false]
    });

    // Suscribirse a cambios en el tipo de operación
    this.form.get('operationType')?.valueChanges.subscribe(operationType => {
      const voucherTypesCodeControl = this.form.get('voucherTypesCodeId');
      const affectsTreasuryControl = this.form.get('affectsTreasury');
      
      if (operationType) {
        console.log('Cargando tipos de comprobante para la operación:', operationType);
        // Habilitar el campo y limpiar la selección
        voucherTypesCodeControl?.enable();
        voucherTypesCodeControl?.setValue('');
        this.loadVoucherTypesByOperation(operationType);
        this.loadVoucherTypesCodeAfip(operationType);
        
        // Pre-establecer "Afecta Tesorería" si el tipo de operación es "treasury"
        if (operationType === 'treasury') {
          affectsTreasuryControl?.setValue(true);
        }
      } else {
        // Deshabilitar el campo y limpiar todo
        voucherTypesCodeControl?.disable();
        voucherTypesCodeControl?.setValue('');
        this.receiptBookTypes = [];
        this.voucherTypesCodeAfip = [];
        
        // Limpiar también el checkbox de tesorería si no hay tipo de operación
        affectsTreasuryControl?.setValue(false);
      }
      
      // Actualizar descripción independientemente
      this.updateDescription();
    });

    // Suscribirse a cambios en el tipo de comprobante para establecer la descripción por defecto
    this.form.get('voucherTypesCodeId')?.valueChanges.subscribe(voucherTypeId => {
      this.updateDescription();
    });

    // Suscribirse a cambios manuales en la descripción
    this.form.get('description')?.valueChanges.subscribe(description => {
      if (this.isEditMode && description !== this.originalDescription) {
        this.userModifiedDescription = true;
      }
    });
  }

  ngOnInit(): void {
    // Cargar tipos de operación desde receipt_book_types
    this.loadOperationTypesFromReceiptBookTypes();
    
    // Cargar tipos de comprobante
    this.loadReceiptBookTypes();
    
    // Verificar si estamos en modo edición
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.type = 'Modificar';
        this.voucherTypeId = +params['id'];
        this.loadVoucherType();
      } else {
        // Si estamos en modo creación, cargar el siguiente código disponible
        this.loadNextCode();
      }
    });

    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = `${this.type} Tipo de Comprobante`;
    }
  }

  loadNextCode() {
    this.parametrosService.getNextVoucherTypeCode().subscribe(
      (response) => {
        this.form.get('code')?.setValue(response.nextCode);
      },
      (error) => {
        console.error('Error al obtener el siguiente código', error);
        this.openSnackBar('Error al obtener el siguiente código disponible', 'Cerrar');
      }
    );
  }

  loadOperationTypesFromReceiptBookTypes() {
    // Cargar tipos de operación manualmente basados en la nueva estructura
    this.operationTypes = [
      { value: 'sales', name: 'Ventas' },
      { value: 'purchases', name: 'Compras' },
      { value: 'stock', name: 'Stock' },
      { value: 'treasury', name: 'Tesorería' }
    ];
    
    // Si necesitas cargar desde API, descomenta esto:
    // this.parametrosService.getReceiptBookTypes().subscribe(
    //   (data) => {
    //     this.operationTypes = data;
    //   },
    //   (error) => {
    //     console.error('Error al cargar tipos de operación desde receipt_book_types', error);
    //     this.openSnackBar('Error al cargar tipos de operación', 'Cerrar');
    //   }
    // );
  }

  loadVoucherTypesByOperation(operationType: string) {
    this.parametrosService.getVoucherTypes(operationType).subscribe(
      (data) => {
        // La respuesta viene agrupada, necesitamos extraer los voucherTypes
        this.receiptBookTypes = [];
        
        if (Array.isArray(data)) {
          // Buscar el grupo que coincide con el operationType
          const matchingGroup = data.find(group => group.operationType === operationType);
          
          if (matchingGroup && matchingGroup.voucherTypes) {
            this.receiptBookTypes = matchingGroup.voucherTypes;
          }
        }
        
        console.log('Tipos de comprobante cargados para', operationType, ':', this.receiptBookTypes);
        // Actualizar descripción después de cargar los tipos
        this.updateDescription();
      },
      (error) => {
        console.error('Error al cargar tipos de comprobante por operación', error);
        this.openSnackBar('Error al cargar tipos de comprobante', 'Cerrar');
      }
    );
  }

  loadReceiptBookTypes() {
    // Este método carga todos los VoucherTypesCode disponibles (no filtrados por operationType)
    // Se usa principalmente para obtener la lista completa
    this.parametrosService.getVoucherTypesCode().subscribe(
      (data) => {
        // Si getVoucherTypesCode() devuelve la estructura simple, la mantenemos
        this.receiptBookTypes = data;
        // Disparar el cambio para establecer la descripción si ya hay un tipo seleccionado
        this.updateDescription();
      },
      (error) => {
        console.error('Error al cargar tipos de comprobante', error);
        this.openSnackBar('Error al cargar tipos de comprobante', 'Cerrar');
      }
    );
  }

  loadVoucherTypesCodeAfip(operationType: string) {
    this.parametrosService.getVoucherTypesCodeAfipByType(operationType).subscribe(
      (data) => {
        this.voucherTypesCodeAfip = data;
      },
      (error) => {
        console.error('Error al cargar comprobantes legales', error);
        this.openSnackBar('Error al cargar comprobantes legales', 'Cerrar');
      }
    );
  }

  triggerVoucherTypeDescriptionUpdate() {
    const voucherTypeId = this.form.get('voucherTypesCodeId')?.value;
    if (voucherTypeId && this.receiptBookTypes.length > 0) {
      const selectedVoucherType = this.receiptBookTypes.find(type => type.id == voucherTypeId);
      if (selectedVoucherType && selectedVoucherType.name) {
        const defaultDescription = `${selectedVoucherType.name}`;
        // Solo establecer la descripción si está vacía o en modo creación
        const currentDescription = this.form.get('description')?.value;
        if (!currentDescription || !this.isEditMode) {
          this.form.get('description')?.setValue(defaultDescription);
        }
      }
    }
  }

  updateDescription() {
    const voucherTypeId = this.form.get('voucherTypesCodeId')?.value;
    const operationType = this.form.get('operationType')?.value;
    
    let defaultDescription = '';
    
    // Obtener nombre del tipo de comprobante usando la estructura tipo?.voucherTypesCode?.name
    if (voucherTypeId && this.receiptBookTypes.length > 0) {
      const selectedVoucherType = this.receiptBookTypes.find(type => type.id == voucherTypeId);
      if (selectedVoucherType?.voucherTypesCode?.name) {
        defaultDescription = selectedVoucherType.voucherTypesCode.name;
      }
    }
    
    // Obtener nombre del tipo de operación usando option.name
    if (operationType && this.operationTypes.length > 0) {
      const selectedOperationType = this.operationTypes.find(option => option.value === operationType);
      if (selectedOperationType?.name) {
        if (defaultDescription) {
          defaultDescription += ` ${selectedOperationType.name}`;
        } else {
          defaultDescription = selectedOperationType.name;
        }
      }
    }
    
    // Solo actualizar si hay contenido válido
    if (defaultDescription && (!this.isEditMode || !this.userModifiedDescription)) {
      this.form.get('description')?.setValue(defaultDescription);
    }
  }

  loadVoucherType() {
    if (this.voucherTypeId) {
      this.parametrosService.getVoucherTypeById(this.voucherTypeId).subscribe(
        (voucherType: VoucherTypes) => {
          // Guardar la descripción original
          this.originalDescription = voucherType.description || '';
          
          this.form.patchValue({
            name: voucherType.name,
            code: voucherType.code,
            voucherTypesCodeId: voucherType.voucherTypesCodeId,
            operationType: voucherType.operationType,
            description: voucherType.description,
            regime: voucherType.regime,
            affectsStock: voucherType.affectsStock || false,
            affectsTreasury: voucherType.affectsTreasury || false
          });

          // Cargar tipos de comprobante filtrados y comprobantes legales si hay tipo de operación
          if (voucherType.operationType) {
            this.loadVoucherTypesByOperation(voucherType.operationType);
            
            // Esperar un poco para asegurar que el DOM y el formulario estén actualizados
            setTimeout(() => {
              // Una vez cargados los receiptBookTypes, establecer el valor seleccionado
              // El campo voucherTypesCodeId debería corresponder a uno de los IDs en receiptBookTypes
              let valueToSet = null;
                
              // Intentar con voucherTypesCodeId
              if (voucherType.voucherTypesCodeId) {
                const exists = this.receiptBookTypes.find(type => type.id == voucherType.voucherTypesCodeId);
                if (exists) {
                  valueToSet = voucherType.voucherTypesCodeId;
                }
              }
              
              // Si no funcionó, intentar con receiptBookTypeId (compatibilidad)
              if (!valueToSet && voucherType.receiptBookTypeId) {
                const exists = this.receiptBookTypes.find(type => type.id == voucherType.receiptBookTypeId);
                if (exists) {
                  valueToSet = voucherType.receiptBookTypeId;
                }
              }
              
              if (valueToSet) {
                this.form.patchValue({
                  voucherTypesCodeId: valueToSet
                });
                // Forzar la detección de cambios
                this.form.get('voucherTypesCodeId')?.updateValueAndValidity();
              }
            }, 500); // Esperar 500ms
            
            this.loadVoucherTypesCodeAfip(voucherType.operationType);
            // Habilitar el campo en modo edición
            this.form.get('voucherTypesCodeId')?.enable();
          }

          // Resetear el flag de modificación manual después de cargar los datos
          setTimeout(() => {
            this.userModifiedDescription = false;
          }, 100);

          // Si hay comprobantes legales seleccionados, cargarlos después de obtener la lista
          // En modo solo lectura, no necesitamos cargar selecciones
        },
        error => {
          console.error('Error al cargar el tipo de comprobante', error);
          this.openSnackBar('Error al cargar el tipo de comprobante', 'Cerrar');
          this.cancelar();
        }
      );
    }
  }

  guardar() {
    if (this.form.valid) {
      const formData = this.form.getRawValue();
      formData.voucherTypesCodeId = Number(formData.voucherTypesCodeId); // Asignar el ID del tipo de comprobante
      
      if (this.isEditMode && this.voucherTypeId) {
        // Modo edición
        this.parametrosService.modificarVoucherType(formData, this.voucherTypeId).subscribe(
          () => {
            this.openSnackBar('Tipo de comprobante modificado correctamente', 'Cerrar');
            this.router.navigate(['/configurar/voucher-types']);
          },
          error => {
            this.openSnackBar(error.message, 'Cerrar');
          }
        );
      } else {
        // Modo creación
        this.parametrosService.guardarVoucherType(formData).subscribe(
          () => {
            this.openSnackBar('Tipo de comprobante creado correctamente', 'Cerrar');
            this.router.navigate(['/configurar/voucher-types']);
          },
          error => {
            this.openSnackBar(error.message, 'Cerrar');
          }
        );
      }
    }
  }

  cancelar() {
    this.router.navigate(['/configurar/voucher-types']);
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 5000,
      verticalPosition: 'bottom',
    });
  }

  // Getters para facilitar el acceso a los controles del formulario
  get name() { return this.form.get('name'); }
  get code() { return this.form.get('code'); }
  get receiptBookTypeId() { return this.form.get('receiptBookTypeId'); }
  get operationType() { return this.form.get('operationType'); }
  get description() { return this.form.get('description'); }
  get regime() { return this.form.get('regime'); }
  get voucherTypeCodeId() { return this.form.get('voucherTypesCodeId'); }
  get affectsStock() { return this.form.get('affectsStock'); }
  get affectsTreasury() { return this.form.get('affectsTreasury'); }
}
